import { deepClone } from './clone';
import { graphOf } from './map';
import { resolveRuleset } from './ruleset';
import { Rng } from './rng';
import { fallbackMission } from './rules/combat';
import { freshTurn } from './rules/turn';
import type { GameConfig, GameState, LootKind, MissionDef, PlayerState, TerritoryState } from './types';

export class ConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ConfigError';
  }
}

/** Cria a partida: missões, distribuição de territórios e fase de posicionamento. */
export function createGame(config: GameConfig): GameState {
  const ruleset = resolveRuleset(config.ruleset);
  const n = config.seats.length;
  if (n < ruleset.minPlayers || n > ruleset.maxPlayers) {
    throw new ConfigError(`número de jogadores inválido: ${n}`);
  }
  const colors = config.seats.map((s) => s.color);
  if (new Set(colors).size !== colors.length) throw new ConfigError('cores repetidas');
  const content = config.content;
  const rng = new Rng(config.seed);

  // Missões secretas
  const missions = rng.shuffle(content.missions);
  const players: PlayerState[] = config.seats.map((seat, id) => {
    let mission: MissionDef = missions[id % missions.length];
    if (mission.type === 'eliminate' && (mission.color === seat.color || !colors.includes(mission.color))) {
      mission = fallbackMission(mission, ruleset.fallbackMissionTerritories);
    }
    return {
      id,
      name: seat.name,
      color: seat.color,
      vocation: seat.vocation,
      kind: seat.kind,
      difficulty: seat.difficulty ?? 'normal',
      alive: true,
      eliminatedBy: null,
      bossKills: 0,
      harmony: 0,
      cards: [],
      loot: [],
      mission,
    };
  });

  // Territórios distribuídos aleatoriamente, 1 tropa em cada
  const graph = graphOf(content.map);
  const territories: Record<string, TerritoryState> = {};
  rng.shuffle(graph.ids).forEach((id, i) => {
    territories[id] = { owner: i % n, troops: 1 };
  });
  const start = ruleset.startingTroops[n];
  if (!start) throw new ConfigError(`sem tropas iniciais para ${n} jogadores`);
  const remaining = players.map((p) => {
    let owned = 0;
    for (const t of Object.values(territories)) if (t.owner === p.id) owned++;
    return Math.max(0, start - owned);
  });

  const lootDeck: LootKind[] = [];
  for (const kind of content.loot) for (let i = 0; i < ruleset.lootCopies; i++) lootDeck.push(kind);

  const state: GameState = {
    seed: config.seed,
    rng: 0,
    info: { content, ruleset },
    phase: 'setup',
    round: 0,
    current: 0,
    setup: { remaining, placedThisGo: 0 },
    reinforcements: 0,
    territories,
    players,
    deck: rng.shuffle(graph.deck),
    discard: [],
    tradeCount: 0,
    lootDeck: rng.shuffle(lootDeck),
    lootDiscard: [],
    boss: null,
    bossDeck: rng.shuffle(content.bosses.map((b) => b.id)),
    bossDiscard: [],
    effects: [],
    effectSeq: 0,
    attacked: Object.fromEntries(players.map((p) => [p.id, [] as string[]])),
    regionFirst: {},
    bounty: null,
    skulls: [],
    defenseUsed: [],
    turn: freshTurn(),
    pending: null,
    eliminationOrder: [],
    result: null,
    seq: 0,
    events: [],
    lastAction: null,
  };
  state.rng = rng.state;
  return state;
}

/** Cópia profunda do estado; o conteúdo e o ruleset (imutáveis) são compartilhados. */
export function cloneState(s: GameState): GameState {
  const { info, ...rest } = s;
  const copy = deepClone(rest) as Omit<GameState, 'info'>;
  return { ...copy, info };
}
